import { displayResults } from './ui/results.js';
import { initToggles } from './ui/toggles.js';
import { initThemeToggle } from './ui/theme.js';
import { loadStateFromUrl } from './ui/share.js';
import { initActions } from './ui/actions.js';
import { initScrollToTop } from './ui/scroll.js';
import { initSelectors } from './ui/selectors.js';
import { initRankings } from './ui/rankings.js';
import { initDrawer } from './ui/drawer.js';
import { initBuilds } from './ui/builds.js';
import { initScanner } from './ui/scanner.js';
import { compareGearSets, fetchGearData } from './api.js';
import { initTranslations, translateGearName } from './utils/formatters.js';

/**
 * Main application entry point.
 */
document.addEventListener('DOMContentLoaded', async () => {
    // Shared Data
    try {
        window.GEAR_DATA = await fetchGearData();
        await initTranslations();
    } catch (err) {
        console.error("Failed to load gear data or translations", err);
        return; // Stop initialization if data is missing
    }
    const gearData = window.GEAR_DATA;

    // Boot UI modules with basic error resilience
    const modules = [
        { name: 'Selectors', init: initSelectors },
        { name: 'Toggles', init: initToggles },
        { name: 'Drawer', init: initDrawer },
        { name: 'Theme', init: initThemeToggle },
        { name: 'Actions', init: initActions },
        { name: 'Rankings', init: initRankings },
        { name: 'Drawer', init: initDrawer }, // initDrawer sets up both Drawers
        { name: 'Builds', init: initBuilds },
        { name: 'Scanner', init: initScanner },
        { name: 'Scroll', init: initScrollToTop }
    ];

    modules.forEach(mod => {
        try {
            mod.init();
        } catch (err) {
            console.error(`Failed to initialize ${mod.name} module:`, err);
        }
    });

    // Translate any rendered UI Option text contents naturally
    if (translateGearName) {
        document.querySelectorAll('option').forEach(opt => {
            // Options use opt.value as the raw English identifier, while textContent contains emojis.
            if (opt.value && opt.value !== '') {
                const englishName = opt.value;
                const translatedName = translateGearName(englishName);
                
                // If a translation exists and is different
                if (translatedName && translatedName !== englishName) {
                    // Cautiously replace only the English substring so Emojis are perfectly preserved
                    opt.textContent = opt.textContent.replace(englishName, translatedName);
                    // Protect this specific, successfully translated element from Chrome
                    opt.setAttribute('translate', 'no');
                }
            }
        });
    }

    // Load state from URL if present
    const stateLoaded = loadStateFromUrl();

    // Register primary form submission listener
    document.getElementById('gearForm').addEventListener('submit', async function (e) {
        e.preventDefault();

        const formErrorsContainer = document.getElementById('form_errors');
        if (formErrorsContainer) {
            formErrorsContainer.classList.add('hidden');
            formErrorsContainer.textContent = '';
        }

        const toggles = {
            attack: document.getElementById('toggle_attack').checked,
            defense: document.getElementById('toggle_defense').checked,
            dragon: document.getElementById('toggle_dragon').checked,
            march_size: document.getElementById('toggle_march_size')?.checked || false,
            sop: document.getElementById('toggle_sop')?.checked || false,
            creatures: document.getElementById('toggle_creatures')?.checked || false,
            balanced_sop: document.getElementById('toggle_balanced')?.checked || false,
            all_stats: document.getElementById('toggle_all_stats')?.checked || false
        };

        const hasScenario = Object.values(toggles).some(val => val === true);
        const hasSection =
            document.getElementById('toggle_gear_section').checked ||
            document.getElementById('toggle_trinket_section').checked ||
            document.getElementById('toggle_pit_section').checked;

        if (!hasScenario || !hasSection) {
            if (formErrorsContainer) {
                formErrorsContainer.textContent = 'Please select at least one scenario and equipment section.';
                formErrorsContainer.classList.remove('hidden');
            }
            return;
        }

        const formData = new FormData(this);
        const set1 = {};
        const set2 = {};

        // Helper to structure data from form entries
        const processEntry = (setObj, key, value) => {
            const parts = key.split('_');
            const slot = parts[1] === 'trinket1' ? 'trinket1' : (parts[1] === 'trinket2' ? 'trinket2' : parts[1]);
            const field = parts[2];

            if (!setObj[slot]) setObj[slot] = {};
            setObj[slot][field] = value;
        };

        for (let [key, value] of formData.entries()) {
            if (value) {
                if (key.startsWith('set1_')) {
                    processEntry(set1, key, value);
                } else if (key.startsWith('set2_')) {
                    processEntry(set2, key, value);
                }
            }
        }

        const slotsToCheck = [
            'helmet', 'chest', 'pants', 'weapon', 'ring', 'boots',
            'trinket1', 'trinket2', 'saddle', 'chanfron', 'peytral'
        ];

        let hasMismatchedSelection = false;
        let hasAnySelection = false;

        // Clear previous visual validation errors
        document.querySelectorAll('.validation-error').forEach(el => {
            el.classList.remove('validation-error');
        });

        for (const slot of slotsToCheck) {
            const hasSet1 = set1[slot] && set1[slot].name;
            const hasSet2 = set2[slot] && set2[slot].name;

            if (hasSet1 || hasSet2) {
                hasAnySelection = true;
            }

            if ((hasSet1 && !hasSet2) || (!hasSet1 && hasSet2)) {
                hasMismatchedSelection = true;

                // Add visual error to the missing selection
                if (hasSet1 && !hasSet2) {
                    const missingEl = document.querySelector(`[name="set2_${slot}_name"]`);
                    if (missingEl) {
                        missingEl.classList.add('validation-error');
                        missingEl.setAttribute('aria-invalid', 'true');
                    }
                } else if (!hasSet1 && hasSet2) {
                    const missingEl = document.querySelector(`[name="set1_${slot}_name"]`);
                    if (missingEl) {
                        missingEl.classList.add('validation-error');
                        missingEl.setAttribute('aria-invalid', 'true');
                    }
                }
            }
        }

        if (!hasAnySelection) {
            if (formErrorsContainer) {
                formErrorsContainer.textContent = 'Please select at least one piece of gear in both Baseline and Target sets to compare.';
                formErrorsContainer.classList.remove('hidden');
            }
            return;
        }

        if (hasMismatchedSelection) {
            if (formErrorsContainer) {
                formErrorsContainer.textContent = 'Please ensure all selected gear has a corresponding selection in the other set (Baseline/Target) to calculate differences.';
                formErrorsContainer.classList.remove('hidden');
            }
            return;
        }

        try {
            // Fetch comparison data from backend
            const result = await compareGearSets(set1, set2, toggles);

            // Render the results into the UI
            displayResults(result, set1, set2);
        } catch (error) {
            console.error('Failed to update comparison results:', error);
        }
    });

    if (stateLoaded) {
        // Auto-submit the form to show results immediately
        document.getElementById('gearForm').requestSubmit();
    }

    // Global listener to remove validation error on change
    document.addEventListener('change', (e) => {
        if (e.target && e.target.classList && e.target.classList.contains('validation-error')) {
            e.target.classList.remove('validation-error');
            e.target.removeAttribute('aria-invalid');
        }
    });

    // How To Use Modal Setup
    const howToUseBtn = document.getElementById('howToUseBtn');
    const howToUseModal = document.getElementById('howToUseModal');
    const closeHowToUseBtn = document.getElementById('closeHowToUseBtn');

    if (howToUseBtn && howToUseModal && closeHowToUseBtn) {
        howToUseBtn.addEventListener('click', () => {
            howToUseModal.classList.add('visible');
            document.body.classList.add('no-scroll');
        });

        closeHowToUseBtn.addEventListener('click', () => {
            howToUseModal.classList.remove('visible');
            document.body.classList.remove('no-scroll');
        });

        howToUseModal.addEventListener('click', (e) => {
            if (e.target === howToUseModal) {
                howToUseModal.classList.remove('visible');
                document.body.classList.remove('no-scroll');
            }
        });

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && howToUseModal.classList.contains('visible')) {
                howToUseModal.classList.remove('visible');
                document.body.classList.remove('no-scroll');
            }
        });
    }
});
